/**
 * Grok dispatcher brain — the company dispatcher (not Inbox Fetch, not Voice).
 *
 * Same xAI Chat family as on-demand Inbox. Different in-repo agent brief.
 * Grok is the default. Classic is the fail-closed safety net (explicit OFF,
 * API down, or an unsafe/confused plan). Local validators still win.
 */
import { liveDeskBases } from "./baseGravity";
import { recordDiagnostic } from "./diagnosticsLog";
import { MOCK_GROK_KEY, mockGrokEnabled, mockGrokFetch } from "./developerMocks";
import { formatCharterOpsBriefPromptBlock } from "./charterOpsBrief";
import { formatCrewPrefAssignPrompt } from "./crewPrefs";
import { XAI_CHAT_URL, XAI_INBOX_MODEL, extractJsonPayload } from "./inbox";
import {
  canAssign,
  hydrateAssignmentAudit,
  hydrateDeclineCooldowns,
  hydrateTripAssignments,
  stateForAssign,
  type AssignmentAuditEvent,
  type CrewCandidate,
  type DeclineCooldown,
  type TripAssignment,
} from "./assignment";
import {
  applyDispatchPlan,
  detectClashes,
  hydrateDispatchPlan,
  proposeDispatchPlan,
  type DispatchBrainInput,
  type DispatchPlan,
  type DispatchPlanAction,
} from "./dispatchBrain";
import { deriveAircraftState } from "./aircraftState";
import { displayFlightNumber } from "./flightNumber";
import {
  groundTicket,
  pickCurrentFlightTicket,
  type GroundingVerdict,
  type MatchState,
} from "./flightGrounding";
import { isCharterAssignable } from "./maintenance";
import type { SiFlightSession } from "./sayintentions";
import type { SimPhaseSample } from "./simDraft";
import { hydrateInboxList } from "./voiceInbox";
import { computeWeightBalance, profileForTail, type WeightBalanceDesk } from "./weightBalance";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyMxBase,
  CrewMember,
  FlightLogEntry,
  MelItem,
  Mission,
  OpsDeskSettings,
  SceneryAirport,
  Squawk,
} from "./types";
import { dutyViewFor, type DutyRoster } from "./duty";
import { resolveAssignWithShift, resolveBoardOverlaps } from "./scheduleShift";
import type { FusedPosition } from "./position";
import { decideClosedSessionGap, type ClosedGapDecision } from "./deskSession";
import { hydrateSceneryAirport } from "./airports";
import { resolveAircraftOperatingCost, resolveCustomerAircraftRequest } from "./aircraftOperatingCost";
import { practicalCharterRangeNm } from "./hawker800xp";
import { hopDistanceNm } from "./techStop";
import { contentXmlScheduleBlocked, icaosFromRoute } from "./contentXmlSchedule";
import { dispatchNotifyDrafts, isUplinkKind, notifyParamsOk, UPLINK_KINDS } from "./acars";
import { hourBandFor, paxBandFor } from "./experienceLog";
import { distanceBand } from "./inboxMintMemory";
import { learnedAcceptGate, learnedPairRank, learnedSlipMinutes, type LearnedPolicy } from "./learnedPolicy";
import type { UplinkKind } from "./types";

export const DISPATCHER_BRAIN_MODES = ["classic", "grok"] as const;
export type DispatcherBrainMode = (typeof DISPATCHER_BRAIN_MODES)[number];

export const DEFAULT_DISPATCHER_BRAIN: DispatcherBrainMode = "grok";

export const GROK_BRAIN_CHECKPOINT_KIND = "northstar.dispatch.grok-checkpoint";

export type GrokBrainCheckpoint = {
  kind: typeof GROK_BRAIN_CHECKPOINT_KIND;
  capturedAtUtc: string;
  reason: "pre_grok";
  missions: Mission[];
  assignments: TripAssignment[];
  inbox: CharterRequest[];
  dispatchPlan: DispatchPlan | null;
  assignmentAudit: AssignmentAuditEvent[];
  declineCooldowns: DeclineCooldown[];
};

export type GrokBrainClock = {
  lastPassAtUtc: string | null;
};

export const EMPTY_GROK_BRAIN_CLOCK: GrokBrainClock = { lastPassAtUtc: null };

/** Clash-only recheck. Pending RFQs always run — RFQ daily-rate is Inbox volume, not a brain lockout. */
export const GROK_BRAIN_IDLE_INTERVAL_MIN = 15;

export const GROK_BRAIN_NO_KEY = "No xAI key — Classic safety net is scheduling. Grok cannot reach the API.";
export const GROK_BRAIN_SAFETY_NET =
  "Grok plan was empty, declined the board, or looked unsafe — Classic safety net filled the gaps.";

export type GrokBrainReviewStatus = "open" | "declined" | "resolved";

export type GrokBrainReview = {
  id: string;
  createdAtUtc: string;
  requestId?: string;
  missionId?: string;
  assignmentId?: string;
  reason: string;
  status: GrokBrainReviewStatus;
};

export type GrokScheduleActionKind =
  | "accept_assign"
  | "decline"
  | "reschedule"
  | "reassign_tail"
  | "reassign_crew"
  | "flag_review"
  | "notify";

export type GrokScheduleAction = {
  id: string;
  kind: GrokScheduleActionKind;
  requestId?: string;
  missionId?: string;
  aircraftId?: string;
  crewId?: string;
  departureIso?: string;
  reason?: string;
  template?: UplinkKind;
  params?: Record<string, string>;
  apply: boolean;
  review: boolean;
};

export type GrokSchedulePlan = {
  actions: GrokScheduleAction[];
  notes: string[];
  usedGrok: boolean;
};

export const GROK_DISPATCH_SYSTEM = [
  "You are the Celtic Air / Northstar company dispatcher on the Northstar Dispatch desk.",
  "You are the product. Classic local math is only a fail-closed safety net when you cannot be reached or your plan is unsafe — not the scheduler you overlay.",
  "Job: read the desk snapshot only and return a structured schedule plan (accept / assign / order / flag). Awareness is the job — range, runway, duty, ferry, overlaps, tech-stops, Pilot-first. Decline only a minority that cannot be made legal.",
  "Stay in aviation / dispatch character. You are the company dispatcher — not a general chatbot, not a copilot, not Grok-as-entertainment.",
  "Desk-snapshot only. Use only ICAOs, tails, crew ids, RFQ ids, mission ids, times, duty, Mx, W&B, range, runway, and tech-stop facts in the snapshot.",
  "MUST: keep the desk certificate framing from the snapshot (default CAR 704 Commuter). Empty ferries and tech-stop hops are never pax revenue. Prefer legal tails and duty that fit.",
  "MUST: in-force duty hours are each crew row's maxFdpMin, maxFlightTimeMin, and minRestMin (dutyTable names the table). Use those numbers. Do not assume CAR 704 hours when dutyTable is a FAR or EASA sim preset.",
  "MUST: honor snapshot practical range and runway facts. A city-pair beyond practicalRangeNm needs the planned tech-stop itinerary (origin → stop → dest), or TBD if techStopPending. Do not invent stock airports or extra stops.",
  "MUST: when two trips overlap on the same tail or crew, slip the later STD (and its inbound ferry) to the next legal slot that respects quiet hours, turn time, and duty — do not leave a clash and do not cancel the later charter to fix the overlap.",
  "MUST: Pilot-first — when a case is normal and legal, accept_assign into a working schedule so the signed-on pilot mainly sees assigned trips.",
  "MUST: schedule most viable Inbox RFQs. An empty board is normal. Duty OFF is a new duty day if the trip fits max flight time.",
  "MUST: if accepting RFQ A leaves the tail away from RFQ B's origin, still accept both — local desk math files a curfew-aware empty ferry (proposal). Never invent ferry times or Navigraph.",
  "MUST: do not invent a home out-and-back when the next revenue already originates at the away field (Boston landing then Boston–TJSJ). Prefer overnight there. Local math cancels the useless A→home→A ferry sandwich.",
  "MUST: do not invent a teleport home or a ferry home because SimConnect dropped after a parked arrival. Last-known on-ground field is career truth.",
  "MUST: when something is weird, consequential, or cannot be made legal — decline that one RFQ or flag_review. Do not silent-insanity the board.",
  "MUST NOT: invent airports, stock tech-stop fields, regulations, weather, METAR, NOTAM, Navigraph, cash, quotes, ledger balances, or ops the snapshot does not show.",
  "MUST NOT: leave character, act as a general assistant, or spend / accept when uncertain.",
  "MUST NOT: auto-decline the whole Inbox or decline every RFQ because the board is empty or a later origin needs a ferry.",
  "MUST NOT: refuse a legal RFQ solely because a runway is short. Under 5000 ft: caution only — PIC decides. Do not decline or rematch solely for runway length.",
  "MUST NOT: override physical or company-limit checks. Duty presets, W&B / MTOW / MZFW / MLW / envelope, range, ferry revenue, and CAR 704 framing are local hard limits — the desk validators win. Runway length is a PIC warning, not a hard limit.",
  "MUST: when snapshot.charterOpsBrief is present, honor that company / flight-type doctrine when choosing among legal RFQs (dest mix, charter type). It may tighten or relax house dest preferences. Never invent ICAOs. Never leave a viable RFQ unscheduled solely because the dest is a hub backup. Crew routeAreaPrefs / preferredTripNm are soft assign bias only under that brief — empty prefs = no forced pick.",
  "MUST: when snapshot.scheduleOnlyContentXmlAirports is true, only accept_assign RFQs whose origin, dest, and tech-stop ICAOs are in snapshot.contentXmlIcaos (the scanned Content.xml / Community list). If that list is empty, leave Inbox pending and note that the desk must rescan Content.xml. Destinations catalog and OurAirports are not eligibility.",
  "Prefer the apply_schedule_plan tool or a JSON object {\"actions\":[...],\"notes\":[...]}. No freeform chat driving state.",
].join(" ");

export const GROK_DISPATCH_MUST_NOT = [
  "invent airports",
  "invent regs",
  "invent weather",
  "invent money",
  "invent ops",
  "leave character",
  "general chatbot",
  "spend or accept when uncertain",
  "auto-decline the whole Inbox",
  "override range",
  "refuse solely for runway",
];

const ACTION_ENUM = ["accept_assign", "decline", "reschedule", "reassign_tail", "reassign_crew", "flag_review", "notify"];

export const GROK_DISPATCH_TOOLS = [
  {
    type: "function" as const,
    function: {
      name: "apply_schedule_plan",
      description:
        "Submit a schedule plan grounded in the desk snapshot. Local validators apply it or surface Decline / Propose-change.",
      parameters: {
        type: "object",
        additionalProperties: false,
        properties: {
          notes: { type: "array", items: { type: "string" } },
          actions: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              properties: {
                kind: { type: "string", enum: ACTION_ENUM },
                requestId: { type: "string" },
                missionId: { type: "string" },
                aircraftId: { type: "string" },
                crewId: { type: "string" },
                departureIso: { type: "string" },
                reason: { type: "string" },
                template: { type: "string", enum: [...UPLINK_KINDS] },
                params: { type: "object", additionalProperties: { type: "string" } },
              },
              required: ["kind"],
            },
          },
        },
        required: ["actions"],
      },
    },
  },
];

export function isDispatcherBrainMode(value: unknown): value is DispatcherBrainMode {
  return value === "classic" || value === "grok";
}

export function hydrateDispatcherBrain(raw?: unknown): DispatcherBrainMode {
  return raw === "classic" ? "classic" : "grok";
}

/**
 * Load / migrate the per-desk brain.
 * Missing or implicit factory Classic (the old default) → Grok.
 * An explicit user-saved Classic, or Classic after a pre-Grok checkpoint, stays Classic.
 */
export function resolveDispatcherBrainOnLoad(input: {
  raw?: unknown;
  userSet?: unknown;
  hasCheckpoint?: boolean;
}): { mode: DispatcherBrainMode; userSet: boolean } {
  if (input.userSet === true) {
    return { mode: hydrateDispatcherBrain(input.raw), userSet: true };
  }
  if (input.userSet === false) {
    return { mode: hydrateDispatcherBrain(input.raw), userSet: false };
  }
  if (input.raw === "classic" && input.hasCheckpoint) {
    return { mode: "classic", userSet: true };
  }
  if (input.raw === "classic") {
    return { mode: DEFAULT_DISPATCHER_BRAIN, userSet: false };
  }
  return { mode: hydrateDispatcherBrain(input.raw), userSet: false };
}

export function dispatcherBrainChip(mode: DispatcherBrainMode, learnedDecisions = 0): string {
  if (mode === "grok") return "Brain: Grok";
  const n = Math.max(0, Math.floor(learnedDecisions));
  return n > 0 ? `Brain: Classic (learned from ${n} Grok decisions)` : "Brain: Classic";
}

/** Fail-closed banner when Grok is on but Nanode / xAI cannot be reached. Classic is quiet. */
export function grokBrainOfflineNotice(input: {
  hasKey: boolean;
  error?: string | null;
  nanodeReachable?: boolean | null;
  mode?: DispatcherBrainMode;
}): string | null {
  if (input.mode !== undefined && hydrateDispatcherBrain(input.mode) !== "grok") return null;
  if (input.nanodeReachable === false) {
    return "Nanode unreachable — fail closed. Inbox/board not pulled; nothing invented.";
  }
  if (input.error?.trim()) return `Grok API failed — Classic safety net. ${input.error.trim()}`;
  if (!input.hasKey) return GROK_BRAIN_NO_KEY;
  return null;
}

/** Pilot (and filtered board) empty copy — never invents trips. */
export function assignedTripsEmptyCopy(input: {
  role: "pilot" | "dispatcher";
  pendingRfqs: number;
  dispatcherBrain: DispatcherBrainMode;
  hasXaiKey: boolean;
  brainError?: string | null;
  nanodeReachable?: boolean | null;
}): string {
  if (input.role !== "pilot") {
    return "No filed trips. Accept a request from Inbox and pick which crew flies it.";
  }
  if (input.nanodeReachable === false) {
    return "Nanode unreachable — fail closed. Assigned trips stay empty until this desk Pulls the company. Nothing invented.";
  }
  const pending = Math.max(0, Math.floor(input.pendingRfqs));
  if (hydrateDispatcherBrain(input.dispatcherBrain) === "grok") {
    const offline = Boolean(input.brainError?.trim()) || !input.hasXaiKey;
    if (pending > 0 && offline) {
      return `Grok cannot reach xAI — Classic safety net is scheduling from ${pending} Inbox RFQ${pending === 1 ? "" : "s"}. Assigned trips appear when a trip is signed to you. Decline / Propose if something looks wrong.`;
    }
    if (pending > 0) {
      return `Grok is scheduling from ${pending} Inbox RFQ${pending === 1 ? "" : "s"}. Assigned trips appear when a trip is signed to you — Decline / Propose as guardrails.`;
    }
    return "No trip assigned to you yet. Inbox has no pending charter RFQs. Grok only files from desk Inbox — it does not invent trips.";
  }
  return "No trip assigned to you yet. Dispatcher files a distinct charter to your crew, then Push / Pull.";
}

export function hydrateGrokBrainClock(raw?: Partial<GrokBrainClock> | null): GrokBrainClock {
  const last = raw?.lastPassAtUtc;
  if (typeof last !== "string" || !last.trim()) return { lastPassAtUtc: null };
  const t = new Date(last);
  if (Number.isNaN(t.getTime())) return { lastPassAtUtc: null };
  return { lastPassAtUtc: t.toISOString() };
}

function cloneMissions(missions: Mission[]): Mission[] {
  return missions.map((m) => ({ ...m, legs: m.legs ? m.legs.map((l) => ({ ...l })) : m.legs }));
}

export function captureGrokBrainCheckpoint(
  input: {
    missions: Mission[];
    assignments: TripAssignment[];
    inbox: CharterRequest[];
    dispatchPlan: DispatchPlan | null;
    assignmentAudit?: AssignmentAuditEvent[];
    declineCooldowns?: DeclineCooldown[];
  },
  now = new Date(),
): GrokBrainCheckpoint {
  return {
    kind: GROK_BRAIN_CHECKPOINT_KIND,
    capturedAtUtc: now.toISOString(),
    reason: "pre_grok",
    missions: cloneMissions(input.missions),
    assignments: input.assignments.map((a) => ({ ...a })),
    inbox: input.inbox.map((r) => ({ ...r })),
    dispatchPlan: input.dispatchPlan ? { ...input.dispatchPlan, actions: [...input.dispatchPlan.actions] } : null,
    assignmentAudit: (input.assignmentAudit ?? []).map((e) => ({ ...e })),
    declineCooldowns: (input.declineCooldowns ?? []).map((c) => ({ ...c })),
  };
}

export function hydrateGrokBrainCheckpoint(raw: unknown): GrokBrainCheckpoint | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<GrokBrainCheckpoint>;
  if (row.kind !== GROK_BRAIN_CHECKPOINT_KIND) return null;
  if (typeof row.capturedAtUtc !== "string") return null;
  return {
    kind: GROK_BRAIN_CHECKPOINT_KIND,
    capturedAtUtc: row.capturedAtUtc,
    reason: "pre_grok",
    missions: Array.isArray(row.missions) ? cloneMissions(row.missions as Mission[]) : [],
    assignments: hydrateTripAssignments(row.assignments),
    inbox: hydrateInboxList(row.inbox),
    dispatchPlan: hydrateDispatchPlan(row.dispatchPlan),
    assignmentAudit: hydrateAssignmentAudit(row.assignmentAudit),
    declineCooldowns: hydrateDeclineCooldowns(row.declineCooldowns),
  };
}

export function restoreGrokBrainCheckpoint(checkpoint: GrokBrainCheckpoint): Pick<
  GrokBrainCheckpoint,
  "missions" | "assignments" | "inbox" | "dispatchPlan" | "assignmentAudit" | "declineCooldowns"
> {
  return {
    missions: cloneMissions(checkpoint.missions),
    assignments: checkpoint.assignments.map((a) => ({ ...a })),
    inbox: checkpoint.inbox.map((r) => ({ ...r })),
    dispatchPlan: checkpoint.dispatchPlan
      ? { ...checkpoint.dispatchPlan, actions: [...checkpoint.dispatchPlan.actions] }
      : null,
    assignmentAudit: checkpoint.assignmentAudit.map((e) => ({ ...e })),
    declineCooldowns: checkpoint.declineCooldowns.map((c) => ({ ...c })),
  };
}

export function hydrateGrokBrainReviews(raw: unknown): GrokBrainReview[] {
  if (!Array.isArray(raw)) return [];
  const out: GrokBrainReview[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Partial<GrokBrainReview>;
    if (typeof row.id !== "string" || !row.id.trim()) continue;
    const status: GrokBrainReviewStatus =
      row.status === "declined" || row.status === "resolved" ? row.status : "open";
    out.push({
      id: row.id,
      createdAtUtc: typeof row.createdAtUtc === "string" ? row.createdAtUtc : new Date().toISOString(),
      requestId: typeof row.requestId === "string" ? row.requestId : undefined,
      missionId: typeof row.missionId === "string" ? row.missionId : undefined,
      assignmentId: typeof row.assignmentId === "string" ? row.assignmentId : undefined,
      reason: typeof row.reason === "string" ? row.reason : "Review",
      status,
    });
  }
  return out;
}

export type GrokDispatchDeskFacts = {
  inbox: CharterRequest[];
  missions: Mission[];
  assignments: TripAssignment[];
  fleet: AircraftSpec[];
  crew: CrewMember[];
  duty: DutyRoster;
  scenery: SceneryAirport[];
  squawks?: Squawk[];
  melItems?: MelItem[];
  weightBalance?: WeightBalanceDesk;
  homeIcao?: string;
  extraBases?: string[];
  bases?: string[];
  mxBases?: readonly CompanyMxBase[];
  enforceDutyClock?: boolean;
  certificateLabel?: string;
  /** Dispatcher-written company / charter-type doctrine. */
  charterOpsBriefTitle?: string;
  charterOpsBrief?: string;
  /** Company Dispatcher Setting — hard-require scanned Content.xml ICAOs. */
  scheduleOnlyContentXmlAirports?: boolean;
  contentXmlIcaos?: string[];
  now?: Date;
  /** Live / last-known sim fix. The brain reads the aircraft, not the next STD. */
  live?: FusedPosition | null;
  flightLogs?: FlightLogEntry[];
  simHistory?: SimPhaseSample[];
  si?: Pick<SiFlightSession, "originIcao" | "destIcao" | "callsign" | "callsignIcao"> | null;
  activeFlightId?: string | null;
  activeFlightAuthority?: "desk" | "megan" | "sim" | null;
  /** Company hourly-cost table. Classic ranking and this snapshot share it. */
  opsDesk?: OpsDeskSettings | null;
  /** Distilled Grok habits. Empty policy leaves Classic unchanged. */
  learnedPolicy?: LearnedPolicy | null;
  /** Desk station callsign. Notify text does not include it. */
  acarsStation?: string;
};

export type GrokDispatchSnapshot = {
  generatedAtUtc: string;
  homeIcao: string;
  extraBases?: string[];
  bases?: string[];
  certificate: string;
  charterOpsBriefTitle?: string;
  charterOpsBrief?: string;
  scheduleOnlyContentXmlAirports?: boolean;
  contentXmlIcaos?: string[];
  rfqs: Array<{
    id: string;
    originIcao: string;
    destIcao: string;
    departureIso: string;
    paxCount: number;
    cargoLb: number;
    flightType: string;
    suggestedAircraftId: string;
    requestedIcaoType?: string;
    requestedClass?: string;
    arriveByIso?: string;
    mxPositioning?: boolean;
    itineraryKind?: string;
    techStopIcao?: string;
    techStopPending?: boolean;
    returnEmpty?: boolean;
    distanceNm?: number | null;
    originRunwayFt?: number;
    destRunwayFt?: number;
    legs?: Array<{ originIcao: string; destIcao: string; paxCount: number; techStop?: boolean; empty?: boolean }>;
  }>;
  board: Array<{
    id: string;
    callsign: string;
    originIcao: string;
    destIcao: string;
    departureIso: string;
    registration: string;
    aircraftId: string;
    status: string;
    paxCount: number;
    eteMin: number;
    flightType: string;
  }>;
  fleet: Array<{
    id: string;
    registration: string;
    icaoType: string;
    status: string;
    assignable: boolean;
    lastAirportIcao?: string;
    maxPax: number;
    rangeNm: number;
    practicalRangeNm: number;
    minRunwayFt: number;
    hourlyDoc: number;
    hourlyDocSource: "tail" | "type" | "unset";
    fuelPerHour: number;
    mxPerHour: number;
    enginePerHour: number;
    insurancePerHour: number;
    rankCruiseKt: number;
    maxPayloadLb?: number;
    mzfwLb?: number;
    mtowLb?: number;
    mlwLb?: number;
  }>;
  crew: Array<{
    id: string;
    name: string;
    ratedIcaoTypes: string[];
    dutyStatus: string;
    dutyLabel: string;
    fdpRemainMin: number;
    flightRemainMin: number;
    routeAreaPrefs?: string;
    preferredTripNm?: number | null;
  }>;
  clashes: Array<{ id: string; kind: string; message: string; missionIds: string[] }>;
  /**
   * What the aircraft is actually doing, from the sim — not from the board.
   * `flying` is null unless position (plus filed plan / SayIntentions when
   * present) backs a ticket. A board row is never "in the air" on its own.
   */
  aircraft: {
    phase: string;
    label: string;
    source: string;
    stale: boolean;
    currentIcao: string | null;
    nearestIcao: string | null;
    nearestNm: number | null;
    parkedIcao: string | null;
    atUtc: string | null;
    note: string;
  } | null;
  /** Same company table Classic auto-assign ranks with. Zero hourly cost is unset. */
  aircraftCosts: {
    types: OpsDeskSettings["aircraftCosts"];
    tails: ReturnType<typeof resolveAircraftOperatingCost>[];
  };
  flying: {
    missionId: string;
    callsign: string;
    originIcao: string;
    destIcao: string;
    verdict: GroundingVerdict;
    atOrigin: MatchState;
    atDest: MatchState;
    filedPlan: MatchState;
    sayIntentions: MatchState;
    sources: string[];
    conflicts: string[];
    status: string;
  } | null;
};

export function buildGrokDispatchSnapshot(
  input: GrokDispatchDeskFacts,
  crews?: CrewCandidate[],
): GrokDispatchSnapshot {
  const now = input.now ?? new Date();
  const squawks = input.squawks ?? [];
  const melItems = input.melItems ?? [];
  const brain: DispatchBrainInput = {
    missions: input.missions,
    assignments: input.assignments,
    fleet: input.fleet,
    crew: input.crew,
    duty: input.duty,
    scenery: input.scenery,
    inbox: input.inbox,
    squawks,
    melItems,
    now,
  };
  const clashes = detectClashes(brain);
  const live = input.live ?? null;
  const current = pickCurrentFlightTicket({
    missions: input.missions,
    assignments: input.assignments,
    fused: live,
    scenery: input.scenery,
    si: input.si,
    now,
    activeFlightId: input.activeFlightId,
    activeFlightAuthority: input.activeFlightAuthority,
    fleet: input.fleet,
  });
  const liveTail = current
    ? input.fleet.find((a) => a.id === current.aircraftId || a.registration === current.registration)
    : undefined;
  const aircraftState = live
    ? deriveAircraftState({
        sample: live,
        scenery: input.scenery,
        parkedIcao: liveTail?.lastAirportIcao,
        live: live.source !== "last_known",
        now,
      })
    : null;
  const grounding = current
    ? groundTicket({
        mission: current,
        fused: live,
        scenery: input.scenery,
        simHistory: input.simHistory,
        fleet: input.fleet,
        logs: input.flightLogs,
        si: input.si,
        missions: input.missions,
        now,
      })
    : null;
  const crewById = new Map<string, { id: string; name: string; ratedIcaoTypes: string[]; routeAreaPrefs?: string; preferredTripNm?: number | null }>();
  for (const c of input.crew) {
    crewById.set(c.id, {
      id: c.id,
      name: c.displayName,
      ratedIcaoTypes: c.ratedIcaoTypes,
      routeAreaPrefs: c.routeAreaPrefs,
      preferredTripNm: c.preferredTripNm,
    });
  }
  for (const c of crews ?? []) {
    if (!crewById.has(c.crewId)) {
      crewById.set(c.crewId, {
        id: c.crewId,
        name: c.crewName,
        ratedIcaoTypes: c.ratedIcaoTypes ?? [],
      });
    }
  }
  return {
    generatedAtUtc: now.toISOString(),
    homeIcao: input.homeIcao || "",
    extraBases: liveDeskBases(input).slice(1),
    bases: liveDeskBases(input),
    certificate: input.certificateLabel || "CAR 704 Commuter",
    charterOpsBriefTitle: input.charterOpsBriefTitle?.trim() || undefined,
    charterOpsBrief: input.charterOpsBrief?.trim() || undefined,
    scheduleOnlyContentXmlAirports: input.scheduleOnlyContentXmlAirports === true ? true : undefined,
    contentXmlIcaos: input.scheduleOnlyContentXmlAirports ? (input.contentXmlIcaos ?? []) : undefined,
    rfqs: input.inbox
      .filter((r) => r.status === "new" && (!r.kind || r.kind === "charter"))
      .map((r) => {
        const origin = hydrateSceneryAirport(r.originIcao, input.scenery);
        const dest = hydrateSceneryAirport(r.destIcao, input.scenery);
        const distanceNm = hopDistanceNm(r.originIcao, r.destIcao, input.scenery);
        const legs = (r.proposedLegs ?? []).map((leg) => ({
          originIcao: leg.originIcao,
          destIcao: leg.destIcao,
          paxCount: leg.paxCount,
          techStop: leg.techStop,
          empty: leg.empty,
        }));
        const ask = resolveCustomerAircraftRequest(r, input.opsDesk);
        return {
          id: r.id,
          originIcao: r.originIcao,
          destIcao: r.destIcao,
          departureIso: r.departureIso,
          paxCount: r.paxCount,
          cargoLb: r.cargoLb,
          flightType: r.flightType,
          suggestedAircraftId: r.suggestedAircraftId,
          requestedIcaoType: ask.icaoType,
          requestedClass: ask.classLabel,
          arriveByIso: r.arriveByIso ?? "",
          mxPositioning: r.mxPositioning,
          itineraryKind: r.itineraryKind,
          techStopIcao: r.techStopIcao,
          techStopPending: r.techStopPending,
          returnEmpty: r.returnEmpty,
          distanceNm: distanceNm != null ? Math.round(distanceNm) : null,
          originRunwayFt: origin?.runwayFt,
          destRunwayFt: dest?.runwayFt,
          legs: legs.length ? legs : undefined,
        };
      }),
    board: input.missions
      .filter((m) => m.status === "pending" || m.status === "in-progress")
      .map((m) => ({
        id: m.id,
        callsign: m.callsign,
        originIcao: m.originIcao,
        destIcao: m.destIcao,
        departureIso: m.departureIso,
        registration: m.registration,
        aircraftId: m.aircraftId,
        status: m.status,
        paxCount: m.paxCount,
        eteMin: m.eteMin,
        flightType: m.flightType,
      })),
    fleet: input.fleet.map((a) => {
      const cost = resolveAircraftOperatingCost(a, input.opsDesk);
      return {
      id: a.id,
      registration: a.registration,
      icaoType: a.icaoType,
      status: a.status,
      assignable: isCharterAssignable(a, squawks, melItems),
      lastAirportIcao: a.lastAirportIcao,
      maxPax: a.maxPax,
      rangeNm: a.rangeNm,
      practicalRangeNm: Math.round(cost.practicalRangeNm || practicalCharterRangeNm(a)),
      minRunwayFt: a.minRunwayFt,
      hourlyDoc: cost.hourlyDoc,
      hourlyDocSource: cost.hourlySource,
      fuelPerHour: cost.fuelPerHour,
      mxPerHour: cost.mxPerHour,
      enginePerHour: cost.enginePerHour,
      insurancePerHour: cost.insurancePerHour,
      rankCruiseKt: cost.cruiseKtas,
      maxPayloadLb: a.maxPayloadLb,
      mzfwLb: a.mzfwLb,
      mtowLb: a.mtowLb,
      mlwLb: a.mlwLb,
    };
    }),
    crew: [...crewById.values()].map((c) => {
      const view = dutyViewFor(input.duty, { id: c.id, name: c.name }, now);
      return {
        id: c.id,
        name: c.name,
        ratedIcaoTypes: c.ratedIcaoTypes,
        dutyStatus: view.status,
        dutyLabel: view.statusLabel,
        dutyTable: view.certificateLabel,
        maxFdpMin: view.limits.maxFdpMin,
        maxFlightTimeMin: view.limits.maxFlightTimeMin,
        minRestMin: view.limits.minRestMin,
        fdpRemainMin: Math.round(view.fdpRemainMin),
        flightRemainMin: Math.round(view.flightRemainMin),
        routeAreaPrefs: c.routeAreaPrefs || undefined,
        preferredTripNm: c.preferredTripNm ?? undefined,
      };
    }),
    clashes: clashes.map((c) => ({
      id: c.id,
      kind: c.kind,
      message: c.message,
      missionIds: c.missionIds,
    })),
    aircraftCosts: {
      types: (input.opsDesk?.aircraftCosts ?? []).map((row) => ({ ...row })),
      tails: input.fleet.map((tail) => resolveAircraftOperatingCost(tail, input.opsDesk)),
    },
    aircraft: aircraftState
      ? {
          phase: aircraftState.phase,
          label: aircraftState.label,
          source: aircraftState.source,
          stale: aircraftState.stale,
          currentIcao: aircraftState.currentIcao,
          nearestIcao: aircraftState.nearestIcao,
          nearestNm: aircraftState.nearestNm != null ? Math.round(aircraftState.nearestNm) : null,
          parkedIcao: aircraftState.parkedIcao,
          atUtc: aircraftState.atUtc ?? null,
          note: aircraftState.note,
        }
      : null,
    flying:
      grounding && current
        ? {
            missionId: current.id,
            callsign: displayFlightNumber(current) || current.callsign,
            originIcao: current.originIcao,
            destIcao: current.destIcao,
            verdict: grounding.verdict,
            atOrigin: grounding.atOrigin,
            atDest: grounding.atDest,
            filedPlan: grounding.plan,
            sayIntentions: grounding.si,
            sources: grounding.sources,
            conflicts: grounding.conflicts,
            status: grounding.status,
          }
        : null,
  };
}

export function buildGrokDispatchPrompt(snapshot: GrokDispatchSnapshot): string {
  return [
    "You are the dispatcher. Build a working company schedule from this desk snapshot only.",
    "Return JSON {\"actions\":[...],\"notes\":[...]} or call apply_schedule_plan.",
    "Pilot-first: normal legal RFQs get accept_assign with a snapshot tail id and crew id. Schedule most viable charters.",
    "Honor snapshot range / duty / tech-stop facts. itineraryKind tech_stop is one ticket (origin → stop → dest). Tech-stop hops and empty ferries are not pax revenue. Do not invent stock airports.",
    "Runway under 5000 ft: caution only — PIC decides. Do not refuse solely for runway length.",
    `Empty board or duty OFF is not a reason to skip. Current company bases: ${(snapshot.bases?.length ? snapshot.bases : [snapshot.homeIcao].filter(Boolean)).join(", ") || "(set Home Base)"}. A later origin at one of those bases after a landing away needs a ferry — accept both; local math files the empty ferry (curfew-aware). A later origin at the field where the aircraft already is does not — overnight there; do not sandwich empty ferries home and back. Do not invent a CYPD / Port Hawkesbury origin unless that ICAO is a current base. Do not invent ferry times.`,
    "Overlaps: slip the later STD (and inbound ferry). Do not cancel the later charter to fix a clash.",
    "Weird / cannot-be-legal minority: decline that RFQ or flag_review. Never auto-decline the whole Inbox.",
    "What the aircraft is doing comes from snapshot.aircraft (sim position / phase) and snapshot.flying (ticket the sim backs) — never from the next STD or the Inbox. flying.verdict mismatch or null means no ticket is being flown: do not treat one as airborne, arrived, or complete, and do not schedule from a field the aircraft is not at.",
    "Do not invent ICAOs, money, weather, Navigraph, ops, or ids that are not in the snapshot.",
    "aircraftCosts is the company variable hourly DOC table (type default, tail override): fuelPerHour is planning only, plus mxPerHour, enginePerHour, and optional insurancePerHour. hourlyDoc is their sum. Types differ — a Hawker hour is not a Citation hour. A saved row replaces that type's editable seed. Zero on a saved component is unset — do not invent a rate or a ledger post, and do not copy one type's rate onto another. Landing, handling, parking, catering, and hotel are trip fees, not part of hourlyDoc. Hangar, monthly hull, and loan are not in this table. When several tails are legal, prefer the customer-requested type or class (requestedIcaoType / requestedClass), then a tail whose practical range covers the city pair and can meet arriveByIso, then the lower hourlyDoc. A dispatcher reassign still wins. Classic posts the ledger.",
    "Empty ferry is not revenue. Local W&B / duty / Mx / range / runway limits win.",
    formatCharterOpsBriefPromptBlock({
      title: snapshot.charterOpsBriefTitle,
      body: snapshot.charterOpsBrief,
    }),
    formatCrewPrefAssignPrompt(),
    snapshot.scheduleOnlyContentXmlAirports
      ? `MUST: schedule only Content.xml / Community ICAOs: ${(snapshot.contentXmlIcaos ?? []).join(", ") || "(empty — leave Inbox pending and say rescan Content.xml)"}. Do not accept_assign a city-pair or tech-stop outside that list.`
      : "",
    "",
    "Desk snapshot:",
    JSON.stringify(snapshot),
  ].join("\n");
}

const PAX_LB = 190;

export function localLimitReasons(
  request: Pick<CharterRequest, "paxCount" | "cargoLb">,
  spec: AircraftSpec,
  weightBalance?: WeightBalanceDesk,
): string[] {
  const reasons: string[] = [];
  if (request.paxCount > spec.maxPax) {
    reasons.push(`${request.paxCount} pax exceeds ${spec.registration} max ${spec.maxPax}.`);
  }
  const payload = request.paxCount * PAX_LB + Math.max(0, request.cargoLb);
  if (spec.maxPayloadLb && payload > spec.maxPayloadLb + 0.5) {
    reasons.push(`Payload ${Math.round(payload)} lb exceeds ${spec.registration} max payload ${spec.maxPayloadLb} lb.`);
  }
  if (spec.oewLb && spec.mzfwLb && spec.oewLb + payload > spec.mzfwLb + 0.5) {
    reasons.push(`OEW+payload would exceed MZFW ${spec.mzfwLb} lb on ${spec.registration}.`);
  }
  if (weightBalance) {
    const profile = profileForTail(weightBalance, spec);
    const wb = computeWeightBalance(profile);
    if (!wb.withinLimits) {
      const hit = wb.reasons.find((r) => /MZFW|MTOW|MLW|payload|envelope/i.test(r));
      if (hit) reasons.push(hit);
    }
  }
  return reasons;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isActionKind(value: unknown): value is GrokScheduleActionKind {
  return (
    value === "accept_assign" ||
    value === "decline" ||
    value === "reschedule" ||
    value === "reassign_tail" ||
    value === "reassign_crew" ||
    value === "flag_review" ||
    value === "notify"
  );
}

function sanitizeNotifyParams(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!/^[a-z]{1,16}$/i.test(key) || typeof value !== "string") continue;
    const clean = value.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+/gi, " ").replace(/\s+/g, " ").trim().slice(0, 80);
    if (clean) out[key.toLowerCase()] = clean;
  }
  return out;
}

/** A board move may slip a trip, not teleport it into history or into next year. */
export const GROK_BOARD_MOVE_HORIZON_MS = 14 * 24 * 60 * 60_000;

export function clampBoardMoveIso(
  raw: string | undefined,
  currentIso: string,
  now: Date,
): { ok: true; departureIso: string } | { ok: false; error: string } {
  const value = (raw ?? "").trim();
  if (!value) return { ok: false, error: "Reschedule did not carry a departure time." };
  const next = new Date(value).getTime();
  if (!Number.isFinite(next)) return { ok: false, error: `Reschedule time ${value} is not a usable date.` };
  if (next < now.getTime() - 60_000) {
    return { ok: false, error: "Reschedule would put the trip in the past." };
  }
  const current = new Date(currentIso).getTime();
  if (Number.isFinite(current) && Math.abs(next - current) > GROK_BOARD_MOVE_HORIZON_MS) {
    return { ok: false, error: "Reschedule moves the trip more than 14 days — needs a hand move." };
  }
  return { ok: true, departureIso: new Date(next).toISOString() };
}

function extractActions(raw: unknown): { rows: Record<string, unknown>[]; notes: string[] } {
  if (!raw || typeof raw !== "object") return { rows: [], notes: [] };
  const obj = raw as Record<string, unknown>;
  const notes = Array.isArray(obj.notes) ? obj.notes.filter((n): n is string => typeof n === "string") : [];
  if (Array.isArray(obj.actions)) {
    return { rows: obj.actions.filter((r): r is Record<string, unknown> => Boolean(r) && typeof r === "object"), notes };
  }
  if (Array.isArray(raw)) {
    return { rows: raw.filter((r): r is Record<string, unknown> => Boolean(r) && typeof r === "object"), notes };
  }
  return { rows: [], notes };
}

export function normalizeGrokSchedulePlan(
  raw: unknown,
  facts: GrokDispatchDeskFacts,
  crews: CrewCandidate[],
  live?: FusedPosition | null,
): GrokSchedulePlan {
  const { rows, notes } = extractActions(raw);
  const requestIds = new Set(facts.inbox.map((r) => r.id));
  const missionIds = new Set(facts.missions.map((m) => m.id));
  const fleetIds = new Set(facts.fleet.map((a) => a.id));
  const crewIds = new Set(facts.crew.map((c) => c.id));
  const sceneryIcaos = new Set(facts.scenery.map((s) => s.icao.toUpperCase()));
  const actions: GrokScheduleAction[] = [];
  /** One ticket per RFQ — a repeated accept/decline would file a ghost sector. */
  const decidedRequests = new Set<string>();

  rows.forEach((row, i) => {
    const kind = isActionKind(row.kind) ? row.kind : null;
    if (!kind) return;
    if (kind === "notify") {
      const template = isUplinkKind(row.template) ? row.template : null;
      const missionId = asString(row.missionId) || undefined;
      if (!template) return;
      if (missionId && !missionIds.has(missionId)) return;
      const params = sanitizeNotifyParams(row.params);
      if (!notifyParamsOk(template, params)) {
        actions.push({
          id: `flag:${i}`,
          kind: "flag_review",
          missionId,
          reason: "Notify is missing a figure from the trip.",
          apply: false,
          review: true,
        });
        return;
      }
      actions.push({
        id: `notify:${missionId ?? i}`,
        kind: "notify",
        missionId,
        template,
        params,
        apply: true,
        review: false,
      });
      return;
    }
    const requestId = asString(row.requestId) || undefined;
    const missionId = asString(row.missionId) || undefined;
    const aircraftId = asString(row.aircraftId) || undefined;
    const crewId = asString(row.crewId) || undefined;
    const departureIso = asString(row.departureIso) || undefined;
    const reason = asString(row.reason) || undefined;

    if (requestId && !requestIds.has(requestId)) return;
    if (requestId && (kind === "accept_assign" || kind === "decline") && decidedRequests.has(requestId)) return;
    if (missionId && !missionIds.has(missionId)) return;
    if (aircraftId && !fleetIds.has(aircraftId)) return;
    if (crewId && !crewIds.has(crewId)) return;

    const request = requestId ? facts.inbox.find((r) => r.id === requestId) : undefined;
    if (request) {
      const origin = request.originIcao.toUpperCase();
      const dest = request.destIcao.toUpperCase();
      if (!sceneryIcaos.has(origin) || !sceneryIcaos.has(dest)) {
        actions.push({
          id: `flag:${i}`,
          kind: "flag_review",
          requestId,
          reason: "RFQ city-pair is not on the Destinations allow-list.",
          apply: false,
          review: true,
        });
        return;
      }
      const xmlBlock = contentXmlScheduleBlocked(
        Boolean(facts.scheduleOnlyContentXmlAirports),
        facts.contentXmlIcaos,
        icaosFromRoute(request),
      );
      if (xmlBlock) {
        actions.push({
          id: `flag:${i}`,
          kind: "flag_review",
          requestId,
          reason: xmlBlock,
          apply: false,
          review: true,
        });
        return;
      }
    }

    const action: GrokScheduleAction = {
      id: `${kind}:${requestId ?? missionId ?? i}`,
      kind,
      requestId,
      missionId,
      aircraftId,
      crewId,
      departureIso,
      reason,
      apply: false,
      review: false,
    };

    if (kind === "flag_review") {
      action.review = true;
      action.reason = reason || "Grok flagged this for Decline / Propose-change.";
      actions.push(action);
      return;
    }

    if (kind === "decline") {
      if (!request || request.status !== "new") return;
      decidedRequests.add(request.id);
      action.apply = true;
      action.reason = reason || "Declined — does not fit the working day.";
      actions.push(action);
      return;
    }

    if (kind === "accept_assign") {
      if (!request || request.status !== "new") return;
      decidedRequests.add(request.id);
      const spec = aircraftId
        ? facts.fleet.find((a) => a.id === aircraftId)
        : facts.fleet.find((a) => a.id === request.suggestedAircraftId);
      const crew = crewId ? crews.find((c) => c.crewId === crewId) : crews[0];
      if (!spec || !crew) {
        action.kind = "flag_review";
        action.review = true;
        action.reason = "No legal tail / crew on the desk snapshot.";
        actions.push(action);
        return;
      }
      const limit = localLimitReasons(request, spec, facts.weightBalance);
      const aircraftState = stateForAssign(
        deriveAircraftState({
          sample: live ?? null,
          scenery: facts.scenery,
          parkedIcao: spec.lastAirportIcao,
          live: Boolean(live && live.source !== "last_known"),
        }),
        spec,
        facts.scenery,
      );
      const gate = canAssign({
        request,
        aircraft: spec,
        aircraftState,
        missions: facts.missions,
        assignments: facts.assignments,
        duty: crew.duty,
        scenery: facts.scenery,
        crewId: crew.crewId,
        crewName: crew.crewName,
        ratedIcaoTypes: crew.ratedIcaoTypes,
        now: facts.now,
        enforceDutyClock: facts.enforceDutyClock !== false,
        mxBases: facts.mxBases,
        opsDesk: facts.opsDesk,
      });
      if (!gate.ok || limit.length) {
        action.kind = "flag_review";
        action.review = true;
        action.reason = limit[0] || gate.hard[0]?.message || "Local limit blocked assign.";
        actions.push(action);
        return;
      }
      action.apply = true;
      action.aircraftId = spec.id;
      action.crewId = crew.crewId;
      actions.push(action);
      return;
    }

    if ((kind === "reschedule" || kind === "reassign_tail" || kind === "reassign_crew") && missionId) {
      const target = facts.missions.find((m) => m.id === missionId);
      const flag = (reason: string) => {
        action.kind = "flag_review";
        action.review = true;
        action.apply = false;
        action.reason = reason;
        actions.push(action);
      };
      if (!target || (target.status !== "pending" && target.status !== "in-progress")) {
        flag("Board move names a trip that is not open on the desk board.");
        return;
      }
      if (kind === "reschedule") {
        const move = clampBoardMoveIso(departureIso, target.departureIso, facts.now ?? new Date());
        if (!move.ok) {
          flag(move.error);
          return;
        }
        action.departureIso = move.departureIso;
      }
      if (kind === "reassign_tail" && !aircraftId) {
        flag("Re-tail did not name a tail on the company fleet.");
        return;
      }
      if (kind === "reassign_crew" && !crewId) {
        flag("Re-crew did not name a pilot on the company crew.");
        return;
      }
      action.apply = true;
      action.review = false;
      actions.push(action);
      return;
    }
  });

  return { actions, notes, usedGrok: true };
}

export type GrokBoardMoveKind = Extract<
  GrokScheduleActionKind,
  "reschedule" | "reassign_tail" | "reassign_crew"
>;

export function isBoardMoveKind(kind: GrokScheduleActionKind): kind is GrokBoardMoveKind {
  return kind === "reschedule" || kind === "reassign_tail" || kind === "reassign_crew";
}

export function boardActionsFromGrokPlan(plan: GrokSchedulePlan): DispatchPlanAction[] {
  const out: DispatchPlanAction[] = [];
  for (const a of plan.actions) {
    if (!a.apply || !a.missionId) continue;
    if (!isBoardMoveKind(a.kind)) continue;
    out.push({
      id: a.id,
      kind: a.kind,
      missionId: a.missionId,
      clashId: a.id,
      from: {},
      to: {
        departureIso: a.departureIso,
        aircraftId: a.aircraftId,
        crewId: a.crewId,
      },
      summary: a.reason || a.kind,
    });
  }
  return out;
}

export function applyGrokBoardActions(
  input: Pick<DispatchBrainInput, "missions" | "assignments" | "fleet" | "scenery">,
  plan: GrokSchedulePlan,
): { missions: Mission[]; assignments: TripAssignment[] } {
  const actions = boardActionsFromGrokPlan(plan);
  if (!actions.length) return { missions: input.missions, assignments: input.assignments };
  const fakePlan = {
    id: "grok-board",
    kind: "northstar.dispatch.plan" as const,
    createdAtUtc: new Date().toISOString(),
    createdByCrewId: "",
    createdByName: "Grok brain",
    status: "accepted" as const,
    boardDateKey: "",
    clashIds: [],
    remainingClashIds: [],
    actions,
    summary: "Grok board order",
    clashNotes: [],
  };
  const applied = applyDispatchPlan(input, fakePlan);
  return applied;
}

export type GrokBrainPassDecision =
  | { ok: false; reason: "classic" | "no_work" | "interval" | "closed_skip" | "no_key" }
  | { ok: true; kind: "schedule"; allowApi: boolean };

export function decideGrokBrainPass(input: {
  mode: DispatcherBrainMode;
  lastPassAtUtc?: string | null;
  pendingRfqs: number;
  clashCount: number;
  hasKey: boolean;
  /** Ignored. RFQ daily-rate is Inbox mint volume only — not a brain lockout. */
  rfqsPerSimDay?: number;
  closedGap?: ClosedGapDecision | null;
  now?: Date;
  /** New Inbox RFQs / first open tick — do not wait out the 15 min API idle. */
  ignoreInterval?: boolean;
}): GrokBrainPassDecision {
  if (hydrateDispatcherBrain(input.mode) !== "grok") return { ok: false, reason: "classic" };
  const pending = input.pendingRfqs > 0;
  const clashes = input.clashCount > 0;
  const closed = input.closedGap;
  if (closed?.isClosedGap) {
    return { ok: false, reason: "closed_skip" };
  }
  if (!pending && !clashes) return { ok: false, reason: "no_work" };
  const now = input.now ?? new Date();
  if (!input.ignoreInterval && input.lastPassAtUtc) {
    const last = new Date(input.lastPassAtUtc);
    if (
      !Number.isNaN(last.getTime()) &&
      (now.getTime() - last.getTime()) / 60_000 < GROK_BRAIN_IDLE_INTERVAL_MIN
    ) {
      return { ok: false, reason: "interval" };
    }
  }
  return { ok: true, kind: "schedule", allowApi: input.hasKey };
}

export const GROK_BRAIN_NO_CAPACITY =
  "No assignable aircraft or crew — RFQs left in Inbox. Decline stays with the Pilot.";

export const GROK_BRAIN_LEFT_PENDING =
  "Could not schedule — RFQs left pending. Decline / Propose stay with the Pilot.";

export function pendingCharterRfqs(inbox: ReadonlyArray<CharterRequest>): CharterRequest[] {
  return inbox.filter((r) => r.status === "new" && (!r.kind || r.kind === "charter"));
}

export function rewriteBrainDeclines(plan: GrokSchedulePlan): GrokSchedulePlan {
  return {
    ...plan,
    actions: plan.actions.map((a) =>
      a.kind === "decline"
        ? {
            ...a,
            kind: "flag_review" as const,
            apply: false,
            review: true,
            reason: a.reason || "Grok would decline — left pending for Pilot Decline / Propose.",
          }
        : a,
    ),
  };
}

/** Empty nonsense, decline-all, or runaway mutation count — use the Classic safety net. */
export function grokPlanLooksUnsafe(plan: GrokSchedulePlan, pendingIds: string[]): boolean {
  if (!pendingIds.length) return false;
  if (!plan.actions.length) return true;
  if (grokPlanWouldEmptyInbox(plan, pendingIds)) return true;
  const accepts = plan.actions.filter((a) => a.kind === "accept_assign" && a.apply);
  const declines = plan.actions.filter((a) => a.kind === "decline");
  if (pendingIds.length >= 2 && !accepts.length && declines.length >= pendingIds.length) return true;
  if (pendingIds.length && plan.actions.length > pendingIds.length * 4) return true;
  return false;
}

/** True when Grok scheduled nothing and would have emptied Inbox (decline-all / no-assign). */
export function grokPlanWouldEmptyInbox(plan: GrokSchedulePlan, pendingIds: string[]): boolean {
  if (!pendingIds.length) return false;
  const accepted = new Set(
    plan.actions
      .filter((a) => a.kind === "accept_assign" && a.apply && a.requestId)
      .map((a) => a.requestId),
  );
  if (pendingIds.some((id) => accepted.has(id))) return false;
  const declined = new Set(
    plan.actions.filter((a) => a.kind === "decline" && a.requestId).map((a) => a.requestId),
  );
  return pendingIds.every((id) => declined.has(id)) || declined.size > 0;
}

/**
 * Grok stays the dispatcher. Classic fills gaps when the plan is empty,
 * decline-all, or otherwise unsafe — the board must not get stuck.
 */
export function finalizeGrokSchedulePlan(
  grokPlan: GrokSchedulePlan,
  facts: GrokDispatchDeskFacts,
  crews: CrewCandidate[],
  live?: FusedPosition | null,
): GrokSchedulePlan {
  const classic = classicLocalSchedulePlan(facts, crews, live);
  const classicAccepts = classic.actions.filter((a) => a.kind === "accept_assign" && a.apply);
  const classicAcceptIds = new Set(classicAccepts.map((a) => a.requestId));
  const grokAccepts = grokPlan.actions.filter((a) => a.kind === "accept_assign" && a.apply);
  const grokAcceptIds = new Set(grokAccepts.map((a) => a.requestId));
  const pendingIds = pendingCharterRfqs(facts.inbox).map((r) => r.id);

  if (grokPlanLooksUnsafe(grokPlan, pendingIds)) {
    if (classicAccepts.length) {
      return {
        ...classic,
        notes: [...grokPlan.notes, GROK_BRAIN_SAFETY_NET],
        usedGrok: false,
      };
    }
    const declines = grokPlan.actions.filter((a) => a.kind === "decline");
    return {
      ...rewriteBrainDeclines({ ...grokPlan, actions: declines }),
      notes: [...grokPlan.notes, GROK_BRAIN_LEFT_PENDING],
      usedGrok: false,
    };
  }

  const actions: GrokScheduleAction[] = [];
  for (const a of grokAccepts) actions.push(a);
  for (const a of classicAccepts) {
    if (!grokAcceptIds.has(a.requestId)) actions.push(a);
  }
  // Board moves (slip / re-tail / re-crew) are how the brief tells Grok to fix an
  // overlap. They were being dropped here, so the model's own slips never landed.
  for (const a of grokPlan.actions) {
    if (a.apply && isBoardMoveKind(a.kind)) actions.push(a);
  }
  for (const a of grokPlan.actions) {
    if (a.kind === "notify" && a.apply && a.template) actions.push(a);
  }
  for (const a of grokPlan.actions) {
    if (a.kind === "decline" && a.requestId && !classicAcceptIds.has(a.requestId) && !grokAcceptIds.has(a.requestId)) {
      actions.push({ ...a, apply: true, review: false });
    }
    if (a.kind === "flag_review" || a.review) {
      if (!classicAcceptIds.has(a.requestId) && !grokAcceptIds.has(a.requestId)) actions.push(a);
    }
  }

  const accepts = actions.filter((a) => a.kind === "accept_assign" && a.apply);
  const declines = actions.filter((a) => a.kind === "decline" && a.apply);
  if (!accepts.length && declines.length) {
    return {
      ...rewriteBrainDeclines({ ...grokPlan, actions: declines }),
      notes: [...grokPlan.notes, GROK_BRAIN_LEFT_PENDING],
    };
  }

  const notes = [...grokPlan.notes];
  if (classicAccepts.length && grokAccepts.length < classicAccepts.length) {
    notes.push(GROK_BRAIN_SAFETY_NET);
  }
  return { actions, notes, usedGrok: grokPlan.usedGrok };
}

export function grokBrainCapacityError(input: {
  fleet: AircraftSpec[];
  crews: CrewCandidate[];
  squawks?: Squawk[];
  melItems?: MelItem[];
}): string | null {
  const assignable = input.fleet.filter((a) =>
    isCharterAssignable(a, input.squawks ?? [], input.melItems ?? []),
  );
  if (!assignable.length) return GROK_BRAIN_NO_CAPACITY;
  if (!input.crews.length) return GROK_BRAIN_NO_CAPACITY;
  return null;
}

export function reviewsFromPlan(plan: GrokSchedulePlan, now = new Date()): GrokBrainReview[] {
  return plan.actions
    .filter((a) => a.review || a.kind === "flag_review")
    .map((a, i) => ({
      id: `rev-${now.getTime().toString(36)}-${i}`,
      createdAtUtc: now.toISOString(),
      requestId: a.requestId,
      missionId: a.missionId,
      reason: a.reason || "Needs Decline or Propose-change.",
      status: "open" as const,
    }));
}

export async function callGrokDispatchBrain(
  apiKey: string,
  snapshot: GrokDispatchSnapshot,
  inboxModel: string,
  fetchImpl: typeof fetch = fetch,
): Promise<unknown> {
  const res = await fetchImpl(XAI_CHAT_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: inboxModel.trim() || XAI_INBOX_MODEL,
      temperature: 0.3,
      response_format: { type: "json_object" },
      tools: GROK_DISPATCH_TOOLS,
      messages: [
        { role: "system", content: GROK_DISPATCH_SYSTEM },
        { role: "user", content: buildGrokDispatchPrompt(snapshot) },
      ],
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`xAI Grok brain HTTP ${res.status}${text ? `: ${text.slice(0, 180)}` : ""}`);
  }
  const payload = (await res.json()) as {
    choices?: { message?: { content?: string; tool_calls?: { function?: { arguments?: string } }[] } }[];
    error?: { message?: string };
  };
  if (payload.error?.message) throw new Error(payload.error.message);
  const msg = payload.choices?.[0]?.message;
  const toolArgs = msg?.tool_calls?.[0]?.function?.arguments;
  if (toolArgs) {
    try {
      return JSON.parse(toolArgs);
    } catch {
      return extractJsonPayload(toolArgs);
    }
  }
  const content = msg?.content;
  if (!content) throw new Error("Grok returned an empty schedule plan.");
  return extractJsonPayload(content);
}

export type RunGrokDispatchPassInput = GrokDispatchDeskFacts & {
  mode: DispatcherBrainMode;
  apiKey?: string;
  inboxModel?: string;
  crews: CrewCandidate[];
  live?: FusedPosition | null;
  lastPassAtUtc?: string | null;
  rfqsPerSimDay?: number;
  closedGap?: ClosedGapDecision | null;
  fetchImpl?: typeof fetch;
  ignoreInterval?: boolean;
};

/** Why the schedule lane ran Classic instead of Grok. Null when Grok drove the plan. */
export type BrainFallbackKind = "classic_mode" | "no_key" | "api_error" | "unsafe_plan" | "no_capacity";

export type RunGrokDispatchPassResult = {
  decision: GrokBrainPassDecision;
  plan: GrokSchedulePlan;
  clock: GrokBrainClock;
  reviews: GrokBrainReview[];
  board: { missions: Mission[]; assignments: TripAssignment[] };
  usedGrok: boolean;
  error?: string;
  fallback?: { kind: BrainFallbackKind; reason: string };
};

export function classicLocalSchedulePlan(
  facts: GrokDispatchDeskFacts,
  crews: CrewCandidate[],
  live?: FusedPosition | null,
): GrokSchedulePlan {
  const actions: GrokScheduleAction[] = [];
  const open = pendingCharterRfqs(facts.inbox).slice().sort((a, b) => {
    const ta = new Date(a.departureIso).getTime();
    const tb = new Date(b.departureIso).getTime();
    return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
  });
  for (const row of open) {
    const rest = {
      aircraftState: stateForAssign(
        deriveAircraftState({
          sample: live ?? null,
          scenery: facts.scenery,
          parkedIcao: facts.fleet.find((a) => a.status === "available")?.lastAirportIcao,
          live: Boolean(live && live.source !== "last_known"),
        }),
        facts.fleet[0] ?? ({ lastAirportIcao: facts.homeIcao } as AircraftSpec),
        facts.scenery,
      ),
      missions: facts.missions,
      assignments: facts.assignments,
      scenery: facts.scenery,
      now: facts.now,
      opsDesk: facts.opsDesk,
      enforceDutyClock: facts.enforceDutyClock,
      mxBases: facts.mxBases,
    };
    const band = distanceBand(row.distanceNm) ?? "";
    const slip = learnedSlipMinutes(facts.learnedPolicy);
    const resolved = resolveAssignWithShift(row, facts.fleet, crews, rest, {
      learnedRank: facts.learnedPolicy
        ? (pair) => learnedPairRank(facts.learnedPolicy, pair.aircraftId, pair.crewId, row.originIcao, band)
        : undefined,
      preferSlipMin: slip ?? undefined,
    });
    if (!resolved) {
      actions.push({
        id: `flag:${row.id}`,
        kind: "flag_review",
        requestId: row.id,
        reason: "No legal Classic assign — Decline or Propose-change.",
        apply: false,
        review: true,
      });
      continue;
    }
    const limit = localLimitReasons(resolved.request, resolved.pick.aircraft, facts.weightBalance);
    if (limit.length) {
      actions.push({
        id: `flag:${row.id}`,
        kind: "flag_review",
        requestId: row.id,
        reason: limit[0],
        apply: false,
        review: true,
      });
      continue;
    }
    const gate = learnedAcceptGate(facts.learnedPolicy, {
      flightType: row.flightType,
      distanceBand: band,
      paxBand: paxBandFor(row.paxCount),
      hourBand: hourBandFor(row.departureIso),
      icaoType: resolved.pick.aircraft.icaoType,
    });
    if (gate.hold) {
      actions.push({
        id: `flag:${row.id}`,
        kind: "flag_review",
        requestId: row.id,
        reason: "Grok usually declined trips like this",
        apply: false,
        review: true,
      });
      continue;
    }
    actions.push({
      id: `accept:${row.id}`,
      kind: "accept_assign",
      requestId: row.id,
      aircraftId: resolved.pick.aircraft.id,
      crewId: resolved.pick.crewId,
      departureIso: resolved.shifted ? resolved.request.departureIso : undefined,
      apply: true,
      review: false,
    });
  }
  const proposed = proposeDispatchPlan({
    missions: facts.missions,
    assignments: facts.assignments,
    fleet: facts.fleet,
    crew: facts.crew,
    duty: facts.duty,
    scenery: facts.scenery,
    inbox: facts.inbox,
    squawks: facts.squawks,
    melItems: facts.melItems,
    now: facts.now,
  });
  const grounded = facts.fleet.filter((row) => row.status === "grounded").map((row) => row.id);
  for (const draft of dispatchNotifyDrafts({ missions: facts.missions, groundedAircraftIds: grounded })) {
    actions.push({
      id: draft.id,
      kind: "notify",
      missionId: draft.missionId,
      template: draft.template,
      params: draft.params,
      apply: true,
      review: false,
    });
  }
  if (proposed?.remainingClashIds.length) {
    actions.push({
      id: "flag:clashes",
      kind: "flag_review",
      reason: proposed.summary,
      apply: false,
      review: true,
    });
  }
  return { actions, notes: ["Classic safety net — Grok unreachable."], usedGrok: false };
}

export async function runGrokDispatchPass(input: RunGrokDispatchPassInput): Promise<RunGrokDispatchPassResult> {
  if (mockGrokEnabled()) {
    input = { ...input, apiKey: MOCK_GROK_KEY, fetchImpl: mockGrokFetch() };
  }
  const now = input.now ?? new Date();
  const pendingRfqs = input.inbox.filter((r) => r.status === "new" && (!r.kind || r.kind === "charter")).length;
  const clashes = detectClashes({
    missions: input.missions,
    assignments: input.assignments,
    fleet: input.fleet,
    crew: input.crew,
    duty: input.duty,
    scenery: input.scenery,
    inbox: input.inbox,
    squawks: input.squawks,
    melItems: input.melItems,
    now,
  });
  const closedGap = input.closedGap ?? decideClosedSessionGap({ pendingCount: pendingRfqs, now, enabled: true });
  const decision = decideGrokBrainPass({
    mode: input.mode,
    lastPassAtUtc: input.lastPassAtUtc,
    pendingRfqs,
    clashCount: clashes.length,
    hasKey: Boolean(input.apiKey?.trim()),
    rfqsPerSimDay: input.rfqsPerSimDay,
    closedGap,
    now,
    ignoreInterval: input.ignoreInterval,
  });
  const clock: GrokBrainClock = { lastPassAtUtc: input.lastPassAtUtc ?? null };
  if (!decision.ok) {
    return {
      decision,
      plan: { actions: [], notes: [], usedGrok: false },
      clock,
      reviews: [],
      board: { missions: input.missions, assignments: input.assignments },
      usedGrok: false,
      fallback:
        decision.reason === "classic"
          ? { kind: "classic_mode", reason: "Dispatcher brain is set to Classic — AI scheduling is off." }
          : undefined,
    };
  }

  const facts: GrokDispatchDeskFacts = { ...input, now };
  const capacityError = grokBrainCapacityError({
    fleet: facts.fleet,
    crews: input.crews,
    squawks: facts.squawks,
    melItems: facts.melItems,
  });
  if (capacityError) {
    const pending = pendingCharterRfqs(facts.inbox);
    const plan: GrokSchedulePlan = {
      actions: pending.map((row) => ({
        id: `flag:${row.id}`,
        kind: "flag_review",
        requestId: row.id,
        reason: capacityError,
        apply: false,
        review: true,
      })),
      notes: [capacityError],
      usedGrok: false,
    };
    return {
      decision,
      plan,
      clock: { lastPassAtUtc: now.toISOString() },
      reviews: reviewsFromPlan(plan, now),
      board: { missions: input.missions, assignments: input.assignments },
      usedGrok: false,
      error: capacityError,
      fallback: { kind: "no_capacity", reason: capacityError },
    };
  }

  let plan: GrokSchedulePlan;
  let usedGrok = false;
  let error: string | undefined;
  let fallback: { kind: BrainFallbackKind; reason: string } | undefined;
  if (decision.allowApi && input.apiKey?.trim()) {
    try {
      const snapshot = buildGrokDispatchSnapshot(facts, input.crews);
      const raw = await callGrokDispatchBrain(
        input.apiKey.trim(),
        snapshot,
        input.inboxModel ?? "",
        input.fetchImpl ?? fetch,
      );
      plan = finalizeGrokSchedulePlan(
        normalizeGrokSchedulePlan(raw, facts, input.crews, input.live),
        facts,
        input.crews,
        input.live,
      );
      usedGrok = plan.usedGrok;
      if (!usedGrok && pendingCharterRfqs(facts.inbox).length) {
        fallback = { kind: "unsafe_plan", reason: GROK_BRAIN_SAFETY_NET };
      }
    } catch (err) {
      error = err instanceof Error ? err.message : "Grok brain failed.";
      recordDiagnostic({
        module: "grok",
        severity: "error",
        message: error,
        stack: err instanceof Error ? err.stack : undefined,
      });
      plan = classicLocalSchedulePlan(facts, input.crews, input.live);
      fallback = { kind: "api_error", reason: error };
    }
  } else {
    plan = classicLocalSchedulePlan(facts, input.crews, input.live);
    if (!input.apiKey?.trim()) {
      error = GROK_BRAIN_NO_KEY;
      fallback = { kind: "no_key", reason: GROK_BRAIN_NO_KEY };
    }
  }

  const grokBoard = applyGrokBoardActions(facts, plan);
  const overlap = resolveBoardOverlaps({
    missions: grokBoard.missions,
    assignments: grokBoard.assignments,
    fleet: facts.fleet,
    crew: facts.crew,
    duty: facts.duty,
    scenery: facts.scenery,
    inbox: facts.inbox,
    squawks: facts.squawks,
    melItems: facts.melItems,
    now,
  });
  const board = { missions: overlap.missions, assignments: overlap.assignments };
  return {
    decision,
    plan,
    clock: { lastPassAtUtc: now.toISOString() },
    reviews: reviewsFromPlan(plan, now),
    board,
    usedGrok,
    error,
    fallback,
  };
}
